import { bind } from 'bind-event-listener';

import { AbstractMentionResource, type MentionContextIdentifier } from '@atlaskit/mention/resource';
import {
	type MentionDescription,
	type MentionDisabledState,
	type MentionDisabledStateInput,
	type MentionNameDetails,
	type MentionStats,
} from '@atlaskit/mention/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { filterAgentsClientSide } from '@atlassian/conversation-assistant-agent/controllers/mention-agents/filter-agents-client-side';
import type {
	MentionAgent,
	MentionAgentSource,
} from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import { type SmartMentionResource } from '@atlassian/smart-mention-resource';

import { agentMentionAnalyticsCallbacks } from './agent-mention-analytics-event';
import { matchesRovoSwitchBackQuery } from './matches-rovo-switch-back-query';
import { buildRovoSwitchBackMention } from './rovo-switch-back-mention';

const AGENT_DROPDOWN_LIMIT = 5;
const EMPTY_QUERY_PEOPLE_LIMIT = 5;
const TYPED_QUERY_PEOPLE_LIMIT = 10;

const agentToMentionDescription = (agent: MentionAgent): MentionDescription => ({
	id: agent.id,
	name: agent.name,
	mentionName: agent.name,
	avatarUrl: agent.avatarUrl ?? undefined,
	userType: 'APP',
	appType: 'agent',
});

/**
 * Public shape passed back when the user selects an agent row.
 * Consumers depend on this narrow shape (id + name).
 */
export type SelectedAgentRef = {
	id: string;
	name: string;
};

type OnAgentSelected = (agent: SelectedAgentRef) => void;
type SelectedAgentIdsGetter = () => readonly string[];

export type RovoChatMentionResourceConfig = {
	/**
	 * Pre-built `SmartMentionResource` for the people typeahead. Hosts
	 * that already own an instance (Townsquare's singleton) pass it in
	 * directly. The default path goes through
	 * `createRovoChatMentionResource()` which builds one internally.
	 */
	peopleResource: SmartMentionResource;
	/**
	 * Optional — when omitted (or `null`), the resource degrades to a
	 * people-only provider. Used when the agent-selection feature gate
	 * is off or no Relay environment is available.
	 */
	agentSource?: MentionAgentSource | null;
	onAgentSelected?: OnAgentSelected;
	getSelectedAgentIds?: SelectedAgentIdsGetter;
	disabledAgentTooltip?: string;
	onAgentMentionDestroyed?: (agentId: string) => void;
	// True when the conversation is bound to a dedicated (non-default) agent
	isDedicatedAgentChat?: boolean;
	// i18n label for the synthetic "Rovo" switch-back row; the row only appears when provided.
	rovoSwitchBackLabel?: string;
	/**
	 * Idle debounce (ms) applied only to the server-side agent
	 * `searchByName` round trip, so we don't issue a Relay request on
	 * every keystroke. When omitted, the agent search fires immediately
	 * (no debounce). Mirrors the `debounceTime` naming used by
	 * `@atlaskit/mention`'s `MentionResourceConfig`.
	 */
	debounceTime?: number;
};

/**
 * Prefix for the per-instance subscription key (see `innerSubscriptionKey`).
 * The people resource keys listeners in a `Map`, so instances sharing one
 * `SmartMentionResource` need distinct keys to avoid evicting each other.
 */
const INNER_SUBSCRIPTION_KEY_PREFIX = 'rovo-chat-mention-resource:people';
let innerSubscriptionKeyCounter = 0;

const isAgentMention = (mention: MentionDescription): boolean =>
	mention.userType === 'APP' && mention.appType === 'agent';

/**
 * Mention provider for Rovo Chat. Holds an injected
 * `SmartMentionResource` for people and an optional `MentionAgentSource`
 * for agents. Each `filter()` fires both in parallel; the dropdown
 * renders people as soon as URS resolves and re-renders with agents
 * appended when they arrive. The first empty-query result is cached so
 * backspacing back to `@` restores the original 5+5 dropdown instantly.
 */
export class RovoChatMentionResource extends AbstractMentionResource {
	/** Unique per-instance key for our listener on the shared people resource. */
	private readonly innerSubscriptionKey = `${INNER_SUBSCRIPTION_KEY_PREFIX}:${++innerSubscriptionKeyCounter}`;
	private readonly peopleResource: SmartMentionResource;
	private readonly agentSource: MentionAgentSource | null;
	private readonly onAgentSelected: OnAgentSelected | undefined;
	private readonly getSelectedAgentIds: SelectedAgentIdsGetter | undefined;
	private disabledAgentTooltip: string | undefined;
	private readonly onAgentMentionDestroyed: ((agentId: string) => void) | undefined;
	private readonly isDedicatedAgentChat: boolean;
	private readonly rovoSwitchBackLabel: string | undefined;
	/** Notified when the active agent selection changes so rendered chips re-evaluate. */
	private readonly disabledStateListeners = new Set<() => void>();

	private currentAbort: AbortController | null = null;

	/** Idle debounce (ms) for the server-side agent search, sourced from
	 *  `config.debounceTime`. `undefined` means fire immediately. */
	private readonly debounceTime: number | undefined;

	/** Idle timer for the debounced server-side agent search. Tracked
	 *  alongside `currentAbort` so it can be cleared on the next keystroke
	 *  and on dispose. */
	private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	/** Most recent search state. People and agent cells are written
	 *  independently as each resolves; `tryEmit()` renders the cells
	 *  tagged with `currentQuery`. */
	private currentQuery = '';
	private currentPeople: {
		mentions: MentionDescription[];
		stats: MentionStats | undefined;
	} | null = null;
	private currentAgents: readonly MentionAgent[] | null = null;
	private currentDisplayedPeople: readonly MentionDescription[] = [];
	private currentDisplayedAgents: readonly MentionAgent[] = [];

	/** First empty-query result, kept so backspacing back to `@` restores instantly. */
	private emptyPeopleSnapshot: {
		mentions: MentionDescription[];
		stats: MentionStats | undefined;
	} | null = null;
	private emptyAgentsSnapshot: readonly MentionAgent[] | null = null;
	private filterSequence = 0;
	private lastReportedFilterSequence: number | null = null;

	constructor(config: RovoChatMentionResourceConfig) {
		super();
		this.peopleResource = config.peopleResource;
		this.agentSource = config.agentSource ?? null;
		this.onAgentSelected = config.onAgentSelected;
		this.getSelectedAgentIds = config.getSelectedAgentIds;
		this.disabledAgentTooltip = config.disabledAgentTooltip;
		this.onAgentMentionDestroyed = config.onAgentMentionDestroyed;
		this.isDedicatedAgentChat = config.isDedicatedAgentChat ?? false;
		this.rovoSwitchBackLabel = config.rovoSwitchBackLabel;
		this.debounceTime = config.debounceTime;

		this.peopleResource.subscribe(this.innerSubscriptionKey, (mentions, query, stats) => {
			this.onPeopleEmit(mentions, query ?? '', stats);
		});
	}

	private onPeopleEmit(
		mentions: MentionDescription[],
		query: string,
		stats: MentionStats | undefined,
	): void {
		// Drop stale URS emissions tagged with a query the user has moved past.
		if (query !== this.currentQuery) {
			return;
		}
		this.currentPeople = { mentions, stats };
		if (this.currentQuery.trim() === '') {
			this.emptyPeopleSnapshot = { mentions, stats };
		}
		this.tryEmit();
	}

	/**
	 * Releases this instance's subscription on the inner people
	 * resource, aborts any in-flight agent fetch, and drops the
	 * disabled-state listeners. After `dispose()` the instance must not
	 * be reused.
	 */
	dispose(): void {
		this.currentAbort?.abort();
		this.currentAbort = null;
		this.clearSearchDebounce();
		this.currentPeople = null;
		this.currentAgents = null;
		this.currentDisplayedPeople = [];
		this.currentDisplayedAgents = [];
		this.emptyPeopleSnapshot = null;
		this.emptyAgentsSnapshot = null;
		this.lastReportedFilterSequence = null;
		this.disabledStateListeners.clear();
		// Always release our subscription on the inner people resource, even
		// when the host owns it. Otherwise the wrapper's closure-bound callback
		// stays registered on the host singleton across remounts.
		this.peopleResource.unsubscribe(this.innerSubscriptionKey);
	}

	filter(query?: string, contextIdentifier?: MentionContextIdentifier): void {
		const rawQuery = query ?? '';
		const trimmed = rawQuery.trim();
		const isEmpty = trimmed.length === 0;

		// Abort the previous keystroke's fetches so their `then` handlers bail,
		// and cancel any pending (not-yet-fired) debounced search.
		this.currentAbort?.abort();
		this.clearSearchDebounce();
		const abort = new AbortController();
		this.currentAbort = abort;

		// Reset cells (snapshots persist — they're the stable empty-query default).
		this.currentQuery = rawQuery;
		this.currentPeople = null;
		this.currentAgents = null;
		this.currentDisplayedPeople = [];
		this.currentDisplayedAgents = [];
		this.filterSequence += 1;

		const restoredEmptySnapshot = isEmpty && this.emptyPeopleSnapshot && this.emptyAgentsSnapshot;
		// Empty-query fast path: restore cached snapshot for instant @-dropdown.
		if (restoredEmptySnapshot) {
			this.currentPeople = this.emptyPeopleSnapshot;
			this.currentAgents = this.emptyAgentsSnapshot;
			queueMicrotask(() => {
				if (abort.signal.aborted) {
					return;
				}
				this.tryEmit();
			});
		}

		this.peopleResource.filter(query, contextIdentifier);
		if (restoredEmptySnapshot) {
			return;
		}

		if (this.agentSource) {
			void this.fetchAgents(trimmed, abort.signal).then((agents) => {
				if (abort.signal.aborted) {
					return;
				}
				this.currentAgents = agents;
				if (isEmpty) {
					this.emptyAgentsSnapshot = agents;
				}
				this.tryEmit();
			});
		}
	}

	/** Emit current cells to subscribers. Waits for people; agents render below.
	 *  Slicing: 5+5 for empty query, 10+5 for typed. */
	private tryEmit(): void {
		if (!this.currentPeople) {
			return;
		}
		const { mentions: people, stats } = this.currentPeople;
		const isEmpty = this.currentQuery.trim().length === 0;
		const peopleLimit = isEmpty ? EMPTY_QUERY_PEOPLE_LIMIT : TYPED_QUERY_PEOPLE_LIMIT;
		const peopleSlice = people.slice(0, peopleLimit);
		this.currentDisplayedPeople = peopleSlice;
		const visibleAgents = this.currentAgents ?? [];
		const visibleAgentDescriptions = visibleAgents
			.map(agentToMentionDescription)
			.slice(0, AGENT_DROPDOWN_LIMIT);
		const agentsSlice = this.withRovoSwitchBack(visibleAgentDescriptions).slice(
			0,
			AGENT_DROPDOWN_LIMIT,
		);
		this.currentDisplayedAgents = visibleAgents.filter((agent) =>
			agentsSlice.some((mention) => mention.id === agentToMentionDescription(agent).id),
		);
		this.reportFilterAnalytics(this.currentDisplayedAgents.length, peopleSlice.length);
		this._notifyListeners(
			{ mentions: [...peopleSlice, ...agentsSlice], query: this.currentQuery },
			stats,
		);
	}

	// Prepend the "Rovo" switch-back row when the gate is on, the user is in an agent context (an
	// agent is mentioned in the input, or the conversation is a dedicated agent chat), and the query
	// targets "Rovo". Deduped against a same-id agent from the source.
	private withRovoSwitchBack(agents: MentionDescription[]): MentionDescription[] {
		const label = this.rovoSwitchBackLabel;
		const inAgentContext =
			(this.getSelectedAgentIds?.().length ?? 0) > 0 || this.isDedicatedAgentChat;
		if (
			!fg('rovo_chat_agent_selection') ||
			!label ||
			!inAgentContext ||
			!matchesRovoSwitchBackQuery(this.currentQuery, label)
		) {
			return agents;
		}
		const rovo = buildRovoSwitchBackMention(label);
		return [rovo, ...agents.filter((agent) => agent.id !== rovo.id)];
	}

	private reportFilterAnalytics(agentResultCount: number, peopleResultCount: number): void {
		if (!this.agentSource || this.currentAgents === null) {
			return;
		}
		if (this.lastReportedFilterSequence === this.filterSequence) {
			return;
		}
		this.lastReportedFilterSequence = this.filterSequence;
		const trimmed = this.currentQuery.trim();
		agentMentionAnalyticsCallbacks.get(this)?.({
			action: trimmed.length === 0 ? 'opened' : 'searched',
			queryLength: trimmed.length,
			agentResultCount,
			peopleResultCount,
		});
	}

	private async fetchAgents(
		trimmed: string,
		signal: AbortSignal,
	): Promise<readonly MentionAgent[]> {
		const source = this.agentSource;
		if (!source) {
			return [];
		}
		try {
			if (trimmed.length === 0) {
				return await source.getAllAgents(signal);
			}
			if (source.canFilterClientSide()) {
				const all = await source.getAllAgents(signal);
				return filterAgentsClientSide(all, trimmed, AGENT_DROPDOWN_LIMIT);
			}
			// Server-side typed search. When a debounce is configured, wait out
			// the idle window first so we don't fire a Relay request on every
			// keystroke; if a newer keystroke arrives the wait is cancelled and
			// this fetch resolves to [] (the newer filter() drives the result).
			if (this.debounceTime !== undefined) {
				const settled = await this.waitForSearchDebounce(this.debounceTime, signal);
				if (!settled) {
					return [];
				}
			}
			const searched = await source.searchByName(trimmed, signal);
			return searched;
		} catch (err) {
			return [];
		}
	}

	/**
	 * Resolves `true` after the idle debounce window, or `false` if the
	 * search is cancelled first (next keystroke / dispose aborts the
	 * signal). Only the server-side search path awaits this.
	 */
	private waitForSearchDebounce(debounceMs: number, signal: AbortSignal): Promise<boolean> {
		return new Promise<boolean>((resolve) => {
			if (signal.aborted) {
				resolve(false);
				return;
			}
			const unbind = bind(signal, {
				type: 'abort',
				listener: () => {
					this.clearSearchDebounce();
					resolve(false);
				},
				options: { once: true },
			});
			this.clearSearchDebounce();
			this.searchDebounceTimer = setTimeout(() => {
				this.searchDebounceTimer = null;
				unbind();
				resolve(!signal.aborted);
			}, debounceMs);
		});
	}

	private clearSearchDebounce(): void {
		if (this.searchDebounceTimer !== null) {
			clearTimeout(this.searchDebounceTimer);
			this.searchDebounceTimer = null;
		}
	}

	isFiltering(query: string): boolean {
		return this.peopleResource.isFiltering?.(query) ?? false;
	}

	shouldHighlightMention(mention: MentionDescription): boolean {
		return this.peopleResource.shouldHighlightMention?.(mention) ?? false;
	}

	supportsMentionNameResolving(): boolean {
		return this.peopleResource.supportsMentionNameResolving?.() ?? false;
	}

	resolveMentionName(id: string): Promise<MentionNameDetails> | MentionNameDetails | undefined {
		return this.peopleResource.resolveMentionName?.(id);
	}

	cacheMentionName(id: string, mentionName: string): void {
		this.peopleResource.cacheMentionName?.(id, mentionName);
	}

	recordMentionSelection(mention: MentionDescription): void {
		// Always notify the inner people resource so people-row analytics
		// and recent-pick state keep working uniformly.
		this.peopleResource.recordMentionSelection?.(mention);

		const queryLength = this.currentQuery.trim().length;
		if (!isAgentMention(mention)) {
			const selectedPersonPosition = this.currentDisplayedPeople.findIndex(
				(person) => person.id === mention.id,
			);
			agentMentionAnalyticsCallbacks.get(this)?.({
				action: 'selected',
				resultType: 'person',
				position: selectedPersonPosition >= 0 ? selectedPersonPosition : undefined,
				queryLength,
			});
			return;
		}
		const selectedAgentPosition = this.currentDisplayedAgents.findIndex(
			(agent) => agent.id === mention.id,
		);
		const selectedAgent = this.currentDisplayedAgents[selectedAgentPosition];
		agentMentionAnalyticsCallbacks.get(this)?.({
			action: 'selected',
			resultType: 'agent',
			agentId: mention.id,
			agentIsVerified: selectedAgent?.isVerified ?? false,
			position: selectedAgentPosition >= 0 ? selectedAgentPosition : undefined,
			queryLength,
		});
		if (!this.onAgentSelected) {
			return;
		}
		this.onAgentSelected({
			id: mention.id,
			name: mention.name ?? '',
		});
	}

	/**
	 * Subscribe to disabled-state changes. Called by `MentionNodeView`
	 * (via the `MentionProvider` interface) so each already-rendered
	 * chip can re-evaluate when the active agent set changes.
	 */
	subscribeToDisabledStateChanges(listener: () => void): () => void {
		this.disabledStateListeners.add(listener);
		return () => {
			this.disabledStateListeners.delete(listener);
		};
	}

	/**
	 * Forwarded to the consumer only when the destroyed id is a known
	 * agent (i.e. present in `getSelectedAgentIds()`). People mentions
	 * are silently ignored so consumers don't need to discriminate.
	 */
	notifyMentionDestroyed(mention: { id: string }): void {
		const ids = this.getSelectedAgentIds?.() ?? [];
		if (!ids.includes(mention.id)) {
			return;
		}
		this.onAgentMentionDestroyed?.(mention.id);
	}

	/**
	 * Notify all subscribers that the disabled-state predicate inputs
	 * have changed (typically because `selectedAgentIds` changed in the
	 * input draft store). Triggers each subscribed `MentionNodeView` to re-run
	 * its `updateState`.
	 */
	notifyDisabledStateChanged(): void {
		this.disabledStateListeners.forEach((listener) => listener());
	}

	setDisabledAgentTooltip(disabledAgentTooltip: string | undefined): void {
		this.disabledAgentTooltip = disabledAgentTooltip;
		this.notifyDisabledStateChanged();
	}

	/**
	 * Agent chip is enabled iff its id is the active agent (`selectedAgentIds.at(-1)`),
	 * otherwise disabled. An empty list means no active agent, so a dismissed
	 * agent's lingering chip stays disabled. Scoped to agent chips via
	 * `userType === 'APP'` (the node carries `userType`, not `appType`).
	 */
	getMentionDisabledState(mention: MentionDisabledStateInput): MentionDisabledState | undefined {
		// Agent chips only — never disable people mentions.
		if (mention.userType !== 'APP') {
			return undefined;
		}

		const ids = this.getSelectedAgentIds?.() ?? [];
		const activeAgentId = ids.at(-1);

		// The active agent is enabled; every other agent chip is disabled.
		if (mention.id === activeAgentId) {
			return undefined;
		}

		return { disabled: true, tooltip: this.disabledAgentTooltip };
	}
}
